/**
 * Apple sends the user's name only on the first authorization, as the `user`
 * field of its form_post to the OAuth callback; the identity token never
 * carries it (the email it does carry, so it is not kept here).
 *
 * The web app's callback route turns that POST into a cookie bound to the
 * token's subject, and the login that the same callback completes reads it
 * back. A cookie, because the response that sets it arrives before the OAuth
 * popup is closed by its opener, and both windows share it.
 */

import { extractProviderUserId } from './oauth-claims.js';

export const APPLE_SIGNUP_PROFILE_COOKIE = 'morfi_apple_signup';
export const APPLE_SIGNUP_PROFILE_MAX_AGE_SECONDS = 600;

export interface AppleSignupProfile {
  readonly name: string;
}

/**
 * Cookie value for the name in Apple's `user` field, bound to the identity
 * token's subject. Plain JSON — set it through a cookie API that URL-encodes values.
 */
export function appleSignupProfileCookieValue(idToken: string, appleUser: string): string | null {
  const sub = extractProviderUserId(idToken);
  const name = parseAppleUserName(appleUser);
  if (sub === null || name === null) return null;
  return JSON.stringify({ sub, name });
}

/** The profile in a raw (URL-encoded) cookie value, only when it was stored for `sub`. */
export function decodeAppleSignupProfileCookie(
  value: string,
  sub: string,
): AppleSignupProfile | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(decodeURIComponent(value));
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const record = parsed as { sub?: unknown; name?: unknown };
  if (record.sub !== sub || typeof record.name !== 'string' || record.name.length === 0) {
    return null;
  }
  return { name: record.name };
}

// Apple's shape: {"name":{"firstName":"…","lastName":"…"},"email":"…"}.
function parseAppleUserName(raw: string): string | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const record = parsed as { name?: { firstName?: unknown; lastName?: unknown } };
  const name = [record.name?.firstName, record.name?.lastName]
    .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
    .map((part) => part.trim())
    .join(' ');
  return name.length > 0 ? name : null;
}
